import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 2-3: brick-heavy with many floating platforms and gaps
export function buildLevel2_3(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [16, 18],
    [32, 35],
    [48, 50],
    [64, 66],
    [80, 82],
    [96, 98],
    [112, 114],
    [128, 130],
    [144, 146],
    [160, 162],
    [176, 178]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // early floating platform
  for (let x = 6; x <= 10; x++) setTile(x, 4, TileType.Brick);
  setTile(8, 4, TileType.Question, { kind: "mushroom" });

  setTile(20, 4, TileType.Question, { kind: "coin" });
  setTile(25, 4, TileType.Question, { kind: "coin" });
  setTile(30, 5, TileType.Question, { kind: "mushroom" });

  // pipes
  const pipes: Array<[number, number]> = [
    [22, 2],
    [38, 3],
    [55, 2],
    [72, 3],
    [100, 2],
    [116, 3],
    [132, 2],
    [148, 3],
    [164, 2],
    [180, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // floating platforms
  for (let x = 42; x <= 46; x++) setTile(x, 4, TileType.Brick);
  for (let x = 47; x <= 51; x++) setTile(x, 7, TileType.Brick);
  setTile(49, 4, TileType.Question, { kind: "coin" });
  setTile(49, 7, TileType.Question, { kind: "mushroom" });

  for (let x = 58; x <= 62; x++) setTile(x, 4, TileType.Brick);
  for (let x = 63; x <= 67; x++) setTile(x, 7, TileType.Brick);
  setTile(60, 4, TileType.Question, { kind: "coin" });
  setTile(65, 7, TileType.Question, { kind: "coin" });

  for (let x = 74; x <= 78; x++) setTile(x, 4, TileType.Brick);
  for (let x = 79; x <= 83; x++) setTile(x, 7, TileType.Brick);
  setTile(76, 4, TileType.Question, { kind: "mushroom" });
  setTile(81, 7, TileType.Question, { kind: "coin" });

  for (let x = 90; x <= 94; x++) setTile(x, 4, TileType.Brick);
  for (let x = 95; x <= 99; x++) setTile(x, 7, TileType.Brick);
  setTile(92, 4, TileType.Question, { kind: "coin" });
  setTile(97, 7, TileType.Question, { kind: "coin" });

  for (let x = 106; x <= 110; x++) setTile(x, 4, TileType.Brick);
  for (let x = 111; x <= 115; x++) setTile(x, 7, TileType.Brick);
  setTile(108, 4, TileType.Question, { kind: "coin" });
  setTile(113, 7, TileType.Question, { kind: "mushroom" });

  for (let x = 122; x <= 126; x++) setTile(x, 4, TileType.Brick);
  for (let x = 127; x <= 131; x++) setTile(x, 7, TileType.Brick);
  setTile(124, 4, TileType.Question, { kind: "coin" });
  setTile(129, 7, TileType.Question, { kind: "coin" });

  for (let x = 136; x <= 140; x++) setTile(x, 4, TileType.Brick);
  for (let x = 141; x <= 145; x++) setTile(x, 7, TileType.Brick);
  setTile(138, 4, TileType.Question, { kind: "coin" });
  setTile(143, 7, TileType.Question, { kind: "coin" });

  for (let x = 152; x <= 156; x++) setTile(x, 4, TileType.Brick);
  for (let x = 157; x <= 161; x++) setTile(x, 7, TileType.Brick);
  setTile(154, 4, TileType.Question, { kind: "coin" });
  setTile(159, 7, TileType.Question, { kind: "coin" });

  for (let x = 168; x <= 172; x++) setTile(x, 4, TileType.Brick);
  for (let x = 173; x <= 177; x++) setTile(x, 7, TileType.Brick);
  setTile(170, 4, TileType.Question, { kind: "mushroom" });
  setTile(175, 7, TileType.Question, { kind: "coin" });

  setTile(184, 4, TileType.Brick);
  setTile(185, 4, TileType.Brick);
  setTile(186, 4, TileType.Brick);

  // final staircase up
  stair(189, 1);
  stair(190, 2);
  stair(191, 3);
  stair(192, 4);
  stair(193, 4);
  stair(194, 4);
  stair(195, 3);
  stair(196, 2);
  stair(197, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 24 },
    { kind: "paratroopa", x: 28 },
    { kind: "goomba", x: 42 },
    { kind: "goomba", x: 50 },
    { kind: "paratroopa", x: 56 },
    { kind: "goomba", x: 64 },
    { kind: "goomba", x: 72 },
    { kind: "paratroopa", x: 78 },
    { kind: "goomba", x: 86 },
    { kind: "goomba", x: 92 },
    { kind: "paratroopa", x: 98 },
    { kind: "goomba", x: 106 },
    { kind: "goomba", x: 114 },
    { kind: "paratroopa", x: 120 },
    { kind: "goomba", x: 126 },
    { kind: "goomba", x: 134 },
    { kind: "paratroopa", x: 142 },
    { kind: "goomba", x: 150 },
    { kind: "goomba", x: 156 },
    { kind: "paratroopa", x: 164 },
    { kind: "goomba", x: 170 },
    { kind: "goomba", x: 176 },
    { kind: "paratroopa", x: 184 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
